/*
  Leza wishlist (localStorage). Stores only product handles, never customer data.
  - One delegated click handler toggles any [data-leza-wishlist-toggle] heart.
  - Cards inserted later (filtering, recommendations, quick view) get their saved look from a generated
    CSS rule, so no MutationObserver is needed; aria-pressed is synced on load and on focus.
  - Header count: [data-leza-wishlist-count] (0 included). Other tabs stay in sync through the storage event.
*/
(() => {
  const KEY = 'leza_wishlist_v1';
  const LEGACY_KEY = 'shopio_wishlist_v1';
  const MAX_ITEMS = 100;
  const TOGGLE = '[data-leza-wishlist-toggle]';

  const read = () => {
    try {
      const raw = localStorage.getItem(KEY);
      if (raw) return JSON.parse(raw).filter((item) => item && typeof item.handle === 'string');
      // One-time migration from the previous theme, keeping only the handle.
      const legacy = JSON.parse(localStorage.getItem(LEGACY_KEY) || '[]');
      return legacy.filter((item) => item && item.handle).map((item) => ({ handle: item.handle, addedAt: Date.now() }));
    } catch (error) {
      return [];
    }
  };

  let items = read();
  const has = (handle) => items.some((item) => item.handle === handle);

  const write = () => {
    try {
      localStorage.setItem(KEY, JSON.stringify(items.slice(0, MAX_ITEMS)));
    } catch (error) {
      /* Private mode or quota: the wishlist simply does not persist. */
    }
  };

  const style = document.createElement('style');
  document.head.appendChild(style);

  const render = () => {
    const selectors = items.map((item) => `${TOGGLE}[data-product-handle="${CSS.escape(item.handle)}"]`);
    style.textContent = selectors.length
      ? `${selectors.join(',')}{color:var(--leza-primary,#733e4a)}${selectors.map((s) => `${s} .icon path`).join(',')}{fill:currentColor}`
      : '';

    document.querySelectorAll(TOGGLE).forEach((button) => {
      button.setAttribute('aria-pressed', has(button.dataset.productHandle));
    });

    document.querySelectorAll('[data-leza-wishlist-count]').forEach((count) => {
      count.textContent = items.length; // shown at 0 too, as on the old site
    });

    document.dispatchEvent(new CustomEvent('leza:wishlist:change', { detail: { handles: items.map((i) => i.handle) } }));
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest(TOGGLE);
    if (!button) return;
    event.preventDefault();
    const handle = button.dataset.productHandle;
    if (!handle) return;
    items = has(handle) ? items.filter((item) => item.handle !== handle) : [{ handle, addedAt: Date.now() }, ...items];
    write();
    render();
    // Old theme: 280ms scale pop on the heart that was pressed (CSS: .is-pop).
    button.classList.remove('is-pop');
    void button.offsetWidth;
    button.classList.add('is-pop');
    setTimeout(() => button.classList.remove('is-pop'), 280);
  });

  document.addEventListener('focusin', (event) => {
    const button = event.target.closest?.(TOGGLE);
    if (button) button.setAttribute('aria-pressed', has(button.dataset.productHandle));
  });

  window.addEventListener('storage', (event) => {
    if (event.key !== KEY) return;
    items = read();
    render();
  });

  window.LezaWishlist = {
    handles: () => items.map((item) => item.handle),
    remove: (handle) => {
      items = items.filter((item) => item.handle !== handle);
      write();
      render();
    },
  };

  render();
})();
